import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface ThemeSwitchProps {
  className?: string;
  showLabels?: boolean;
  variant?: 'dropdown' | 'buttons' | 'toggle';
}

const ThemeSwitch: React.FC<ThemeSwitchProps> = ({ 
  className = '', 
  showLabels = false,
  variant = 'dropdown'
}) => {
  const { theme, effectiveTheme, setTheme } = useTheme();

  // Icon components as SVG
  const SunIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="5"/>
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
    </svg>
  );

  const MoonIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );

  const AutoIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
      <line x1="8" y1="21" x2="16" y2="21"/>
      <line x1="12" y1="17" x2="12" y2="21"/>
    </svg>
  );

  const getThemeIcon = (themeType: string) => {
    switch (themeType) {
      case 'light':
        return <SunIcon />;
      case 'dark':
        return <MoonIcon />;
      case 'auto':
        return <AutoIcon />;
      default:
        return <AutoIcon />;
    }
  };

  const getThemeLabel = (themeType: string) => {
    switch (themeType) {
      case 'light':
        return 'Light';
      case 'dark':
        return 'Dark';
      case 'auto':
        return 'Auto';
      default:
        return 'Auto';
    }
  };

  if (variant === 'dropdown') {
    return (
      <div className={`dropdown ${className}`}>
        <button
          className="btn btn-outline-secondary dropdown-toggle d-flex align-items-center gap-2"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          title={`Current theme: ${getThemeLabel(theme)} (${effectiveTheme})`}
        >
          {getThemeIcon(theme)}
          {showLabels && <span>{getThemeLabel(theme)}</span>}
        </button>
        <ul className="dropdown-menu dropdown-menu-end">
          <li>
            <button
              className={`dropdown-item d-flex align-items-center gap-2 ${theme === 'light' ? 'active' : ''}`}
              onClick={() => setTheme('light')}
            >
              <SunIcon />
              Light
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item d-flex align-items-center gap-2 ${theme === 'dark' ? 'active' : ''}`}
              onClick={() => setTheme('dark')}
            >
              <MoonIcon />
              Dark
            </button>
          </li>
          <li>
            <button
              className={`dropdown-item d-flex align-items-center gap-2 ${theme === 'auto' ? 'active' : ''}`}
              onClick={() => setTheme('auto')}
            >
              <AutoIcon />
              Auto
            </button>
          </li>
        </ul>
      </div>
    );
  }

  if (variant === 'buttons') {
    return (
      <div className={`btn-group ${className}`} role="group" aria-label="Theme selection">
        <button
          type="button"
          className={`btn ${theme === 'light' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setTheme('light')}
          title="Light theme"
        >
          <SunIcon />
          {showLabels && <span className="ms-1">Light</span>}
        </button>
        <button
          type="button"
          className={`btn ${theme === 'dark' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setTheme('dark')}
          title="Dark theme"
        >
          <MoonIcon />
          {showLabels && <span className="ms-1">Dark</span>}
        </button>
        <button
          type="button"
          className={`btn ${theme === 'auto' ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setTheme('auto')}
          title="Auto theme (follows system)"
        >
          <AutoIcon />
          {showLabels && <span className="ms-1">Auto</span>}
        </button>
      </div>
    );
  }

  if (variant === 'toggle') {
    return (
      <button
        type="button"
        className={`btn btn-outline-secondary ${className}`}
        onClick={() => setTheme(effectiveTheme === 'light' ? 'dark' : 'light')}
        title={`Switch to ${effectiveTheme === 'light' ? 'dark' : 'light'} theme`}
      >
        {effectiveTheme === 'light' ? <MoonIcon /> : <SunIcon />}
        {showLabels && (
          <span className="ms-2">
            {effectiveTheme === 'light' ? 'Dark' : 'Light'}
          </span>
        )}
      </button>
    );
  }

  return null;
};

export default ThemeSwitch;

// Additional utility component for simple theme toggle
export const SimpleThemeToggle: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { effectiveTheme, toggleTheme } = useTheme();

  return (
    <button
      type="button"
      className={`btn btn-link p-2 ${className}`}
      onClick={toggleTheme}
      title={`Switch to ${effectiveTheme === 'light' ? 'dark' : 'light'} theme`}
      style={{
        border: 'none',
        background: 'none',
        color: 'var(--text-secondary)',
        transition: 'color var(--transition-normal)',
      }}
    >
      {effectiveTheme === 'light' ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="5"/>
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
        </svg>
      )}
    </button>
  );
};

// Theme indicator component (shows current effective theme)
export const ThemeIndicator: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { theme, effectiveTheme } = useTheme();

  return (
    <div className={`d-flex align-items-center gap-2 ${className}`}>
      <div
        className="rounded-circle"
        style={{
          width: '8px',
          height: '8px',
          backgroundColor: effectiveTheme === 'light' ? '#fbbf24' : '#6366f1',
        }}
      />
      <small className="text-muted">
        {theme === 'auto' ? `Auto (${effectiveTheme})` : effectiveTheme}
      </small>
    </div>
  );
};